import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Minimize2, Maximize2, X, Copy, Video } from "lucide-react";
import { toast } from "sonner";
import { DailyVideoCall } from "./DailyVideoCall";
import { cn } from "@/lib/utils";
import type { ActiveDailyCall } from "@/hooks/useVideoRingNotifications";

interface DailyVideoCallModalProps {
  activeDailyCall: ActiveDailyCall;
  onClose: () => void;
}

export function DailyVideoCallModal({ activeDailyCall, onClose }: DailyVideoCallModalProps) {
  const [isMinimized, setIsMinimized] = useState(false);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setDuration((d) => d + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
  };

  const handleCopyLink = () => {
    if (activeDailyCall.clientJoinUrl) {
      navigator.clipboard.writeText(activeDailyCall.clientJoinUrl);
      toast.success("Link copiado!");
    }
  };

  return (
    <div
      className={cn(
        "fixed z-[200] transition-all duration-300 ease-in-out overflow-hidden flex flex-col",
        isMinimized
          ? "bottom-4 right-4 w-80 h-52 rounded-2xl shadow-2xl border bg-background"
          : "inset-0 z-[110] flex items-center justify-center p-4"
      )}
      style={isMinimized ? undefined : { pointerEvents: "none" }}
    >
      {/* Backdrop — only expanded */}
      {!isMinimized && (
        <div className="absolute inset-0 bg-black/40 pointer-events-none" />
      )}

      {/* Container */}
      <div
        className={cn(
          "relative flex flex-col overflow-hidden pointer-events-auto",
          isMinimized
            ? "w-full h-full rounded-2xl"
            : "w-[95vw] max-w-5xl h-[85dvh] bg-background border rounded-xl shadow-2xl animate-in zoom-in-95 fade-in duration-200"
        )}
      >
        {/* Header */}
        <div className={cn(
          "flex items-center justify-between shrink-0 border-b bg-background",
          isMinimized ? "px-2 py-1" : "px-4 py-2"
        )}>
          <div className="flex items-center gap-1.5">
            <Video className={cn(isMinimized ? "h-3 w-3" : "h-4 w-4", "text-primary")} />
            {!isMinimized && <span className="text-sm font-medium">Videochamada</span>}
            <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0">
              {formatDuration(duration)}
            </Badge>
          </div>
          <div className="flex items-center gap-0.5">
            {activeDailyCall.clientJoinUrl && (
              <Button
                variant="ghost"
                size="icon"
                className={cn(isMinimized ? "h-6 w-6" : "h-7 w-7")}
                onClick={handleCopyLink}
              >
                <Copy className="h-3 w-3" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              className={cn(isMinimized ? "h-6 w-6" : "h-7 w-7")}
              onClick={() => setIsMinimized(!isMinimized)}
            >
              {isMinimized ? <Maximize2 className="h-3 w-3" /> : <Minimize2 className="h-3.5 w-3.5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                isMinimized ? "h-6 w-6" : "h-7 w-7",
                "hover:bg-destructive/10 hover:text-destructive"
              )}
              onClick={onClose}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {/* Video — always mounted, never unmounted */}
        <div className="flex-1 min-h-0">
          <DailyVideoCall
            roomUrl={activeDailyCall.roomUrl}
            token={activeDailyCall.token}
            meetingId={activeDailyCall.meetingId}
            onMeetingEnded={onClose}
          />
        </div>
      </div>
    </div>
  );
}
